import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface ProductLimitBannerProps {
    maxProducts: number;
    variant?: 'flow' | 'absolute';
}

/**
 * The wall a vendor hits when the catalogue is full.
 *
 * `flow` cancels the page's own padding so the bar reaches both edges;
 * `absolute` pins it over a page that paints its own background.
 */
export const ProductLimitBanner: React.FC<ProductLimitBannerProps> = ({ maxProducts, variant = 'flow' }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <div
            className={cn(
                'animate-in slide-in-from-top-4 duration-300',
                variant === 'absolute'
                    ? 'absolute inset-x-0 top-0 z-[9999]'
                    : '-mx-4 -mt-6 mb-6 sm:-mx-6 md:-mx-8',
            )}
        >
            <div className="border-b bg-background/95 px-4 py-4 backdrop-blur-md sm:px-5 sm:py-5 md:px-6">
                <div className="mx-auto flex max-w-[1200px] flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:gap-5">
                    <div className="flex flex-1 items-center gap-3 sm:gap-5">
                        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 sm:size-12">
                            <AlertCircle className="size-6 text-destructive sm:size-7" />
                        </span>

                        <div className="min-w-0 flex-1">
                            <p className="mb-0.5 text-sm font-medium sm:text-base">
                                {t('PAGES.NEW_PRODUCT.LIMIT_REACHED_TITLE')}
                            </p>
                            <p className="text-xs leading-relaxed text-muted-foreground sm:text-[13px]">
                                {t('PAGES.NEW_PRODUCT.LIMIT_REACHED_DESCRIPTION', { max: maxProducts })}
                            </p>
                        </div>
                    </div>

                    <Button
                        variant="outline"
                        className="w-full shrink-0 sm:w-auto"
                        onClick={() => navigate('/products/catalog')}
                    >
                        {t('PAGES.NEW_PRODUCT.BACK_TO_CATALOG')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
